import { useCallback } from "react";
import { v4 as uuidv4 } from "uuid";
import Cookies from "js-cookie";
import { answerTrackerQuestionMap, getLpSessionCustomData } from "../utils";
import axios from "axios";

export const LOCAL_STORAGE_SESSION_IDENTITY_SESSION_ID_KEY =
    "BWS_ANSWER_TRACKER_SESSION_ID";

// bws-lp landing container: the SessionIdentity row it created on arrival
// (customData kellyFunnelCell / stableId / lpExperiment). The quiz continues
// that row, so the answers and the email hash land on it.
export const LP_SESSION_ID_COOKIE = "bws_answer_session_id";
// The last container id adopted, so each one is adopted once.
const LP_ADOPTED_SESSION_ID_KEY = "BWS_LP_ADOPTED_SESSION_ID";
// Our uuids and Core's session_<nanoid> ids.
const SESSION_ID_PATTERN = /^[A-Za-z0-9_-]{8,128}$/;

/**
 * Adopt the landing container's session id as BWS_ANSWER_TRACKER_SESSION_ID:
 * `sid` on a handoff URL (it comes with `lp`; a bare `sid` is someone else's
 * param), else the bws_answer_session_id cookie. Each id is adopted once, so
 * a different id the server returns later (and the tracker stores) is kept.
 * Runs at boot (index.tsx, before the quiz's first redirect drops the query
 * string) and before every track call.
 */
export function adoptLpSessionId(search: string = window.location.search): void {
    const params = new URLSearchParams(search);
    const fromUrl = params.get("lp") ? params.get("sid") : null;
    const id = [fromUrl, Cookies.get(LP_SESSION_ID_COOKIE)].find(
        (value): value is string => !!value && SESSION_ID_PATTERN.test(value)
    );
    if (!id) return;
    try {
        if (localStorage.getItem(LP_ADOPTED_SESSION_ID_KEY) === id) return;
        localStorage.setItem(LOCAL_STORAGE_SESSION_IDENTITY_SESSION_ID_KEY, id);
        localStorage.setItem(LP_ADOPTED_SESSION_ID_KEY, id);
    } catch {
        // Storage blocked: the tracker falls back to its own id, as before.
    }
}

export function getAnswerTrackerSessionId() {
    adoptLpSessionId();
    const sessionId = localStorage.getItem(
        LOCAL_STORAGE_SESSION_IDENTITY_SESSION_ID_KEY
    );

    if (!sessionId) {
        const newSessionId = uuidv4();
        localStorage.setItem(
            LOCAL_STORAGE_SESSION_IDENTITY_SESSION_ID_KEY,
            newSessionId
        );
        return newSessionId;
    }

    return sessionId;
}

const setAnswerTrackerSessionId = (id: string) => {
    localStorage.setItem(LOCAL_STORAGE_SESSION_IDENTITY_SESSION_ID_KEY, id);
};

export function useAnswerTracker() {
    const trackAnswer = useCallback(
        async (questionName: string, answer: any, allAnswers: any) => {
            let data = {} as Record<string, any>;
            let shouldTrackAnswer = !questionName.endsWith("Splash");

            data.sessionId = getAnswerTrackerSessionId();
            if (allAnswers["email"]) {
                data.email = allAnswers["email"];
            }

            // Include hs_offer from localStorage if present
            const hsOffer = localStorage.getItem("hs_offer");
            if (hsOffer) {
                data.customData = {
                    ...data.customData,
                    quiz_promotional_offer: hsOffer,
                };
            }

            if (answerTrackerQuestionMap[questionName]) {
                const mappedData = answerTrackerQuestionMap[questionName](
                    answer,
                    allAnswers
                );
                if (mappedData) {
                    data = { ...data, ...mappedData };
                } else {
                    // nothing to track yet
                    shouldTrackAnswer = false;
                }
            } else {
                data.customData = {
                    [questionName]: answer,
                };
            }

            const comment = allAnswers[`${questionName}-Comment`];
            if (comment) {
                data.customData[`${questionName}-Comment`] = comment;
            }

            // kelly_funnel: re-assert the container's cell on the row.
            const lpCustomData = getLpSessionCustomData();
            if (Object.keys(lpCustomData).length > 0) {
                data.customData = { ...data.customData, ...lpCustomData };
            }

            const devHosts = ["localhost", "quizbeta.builtwithscience.com"];
            const checkoutv2TestMode =
                localStorage.getItem("checkoutv2TestMode") === "enabled";
            const sessionTrackerEndpoint =
                devHosts.includes(window.location.hostname) ||
                checkoutv2TestMode
                    ? "https://staging.core.builtwithscience.com/internal/public/session/track"
                    : "https://core.builtwithscience.com/internal/public/session/track";

            if (shouldTrackAnswer) {
                const response = await axios.post(sessionTrackerEndpoint, data);
                if (response.data.data.sessionId) {
                    setAnswerTrackerSessionId(response.data.data.sessionId);
                }
                const iacConnectionId = localStorage.getItem("iacConnectionId");
                if (iacConnectionId) {
                    axios
                        .post(
                            "https://staging.ws.builtwithscience.com/message/publish",
                            {
                                connectionId: iacConnectionId,
                                event: "quiz",
                                message: JSON.stringify(data),
                            }
                        )
                        .catch((error) => {
                            console.error("Error sending IAC message:", error);
                        });
                }
            } else {
                console.log("skip tracking answer", questionName, answer);
            }
        },
        []
    );

    return { trackAnswer };
}
